import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote, Pencil, ArrowLeft } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewTestimonial = () => {
  const { testimonialId } = useParams();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestimonial();
  }, [testimonialId]);

  const fetchTestimonial = async () => {
    try {
      const response = await testimonialService.getTestimonial(testimonialId);
      setTestimonial(response.data.data);
    } catch (err) {
      Alert.error('Failed to load testimonial details');
      navigate('/testimonials');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStars = (rating) => '⭐'.repeat(rating);

  if (loading) {
    return (
      <PageLayout title="Testimonial Details" icon={MessageSquareQuote}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
        </div>
      </PageLayout>
    );
  }

  if (!testimonial) return null;

  const imageSrc = testimonial.image_base64
    ? `data:image/jpeg;base64,${testimonial.image_base64}`
    : null;

  return (
    <PageLayout
      title="Testimonial Details"
      icon={MessageSquareQuote}
      actions={
        <>
          <Button icon={Pencil} onClick={() => navigate(`/testimonials/edit/${testimonialId}`)}>
            Edit Testimonial
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/testimonials')}>
            Back to Testimonials
          </Button>
        </>
      }
    >
      <div className="bg-primary rounded-card p-4 mb-4 flex flex-wrap items-center gap-3">
        {imageSrc && !imageError ? (
          <img src={imageSrc} alt={testimonial.full_name} className="w-12 h-12 rounded-full object-cover border-2 border-white" onError={() => setImageError(true)} />
        ) : (
          <span className="text-3xl">👤</span>
        )}
        <div>
          <h2 className="text-lg font-semibold text-secondary">{testimonial.full_name}</h2>
          <p className="text-sm text-gray-600">{testimonial.location}</p>
        </div>
        <StatusBadge status={testimonial.is_active ? 'active' : 'inactive'} />
      </div>

      <div className="space-y-4">
        <DetailCard title="Customer Information">
          <DetailRow label="Testimonial ID" value={testimonial.id} />
          <DetailRow label="Full Name" value={testimonial.full_name} />
          <DetailRow label="Location" value={testimonial.location} />
          <DetailRow label="Status" value={<StatusBadge status={testimonial.is_active ? 'active' : 'inactive'} />} />
        </DetailCard>

        <DetailCard title="Testimonial Content">
          <DetailRow label="Rating" value={`${renderStars(testimonial.rating)} (${testimonial.rating}/5)`} />
          <DetailRow label="Testimonial Text" value={<p className="italic whitespace-pre-wrap">"{testimonial.testimonial_text}"</p>} />
        </DetailCard>

        <DetailCard title="System Information">
          <DetailRow label="Created By" value={testimonial.created_by || 'System'} />
          <DetailRow label="Created At" value={formatDate(testimonial.created_at)} />
          <DetailRow label="Last Updated By" value={testimonial.updated_by || 'N/A'} />
          <DetailRow label="Last Updated At" value={formatDate(testimonial.updated_at)} />
        </DetailCard>
      </div>
    </PageLayout>
  );
};

export default ViewTestimonial;
